import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Mail,
} from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { type ConnectedProps, connect } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { clearErrorMessage, logIn, logOut, saveToken } from "@/actions/auth";
import * as routes from "@/constants/routes";
import { strings } from "@/I18n";
import { sendPasswordReset, setupRecaptcha } from "@/utils/firebase";
import { getWhiteLabelLogo } from "@/utils/utils";
import { ResponsiveModal } from "@/v2/ui/composed/responsive-modal";
import { Alert, AlertDescription } from "@/v2/ui/primitives/alert";
import { Button } from "@/v2/ui/primitives/button";
import { Input } from "@/v2/ui/primitives/input";
import { Label } from "@/v2/ui/primitives/label";

const mapDispatchToProps = {
  logIn,
  saveToken,
  logOut,
  clearErrorMessage,
};

const connector = connect(null, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ForgotPasswordScreen: React.FC<PropsFromRedux> = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [isCapchaFilled, setIsCapchaFilled] = useState<boolean>(false);
  const [isEmailSent, setIsEmailSent] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    setIsCapchaFilled(false);
    setupRecaptcha(() => {
      setMessage("");
      setIsCapchaFilled(true);
    });
  }, []);

  const resetPassword = (e?: React.FormEvent) => {
    e?.preventDefault();

    if (!isCapchaFilled) {
      setMessage(strings("login.captchaError"));
      return;
    }

    if (!email) {
      setMessage(strings("login.enterEmail"));
      return;
    }

    setLoading(true);
    setMessage("");

    sendPasswordReset(email)
      .then(() => {
        setLoading(false);
        setIsEmailSent(true);
      })
      .catch((err: Error) => {
        setMessage(err.message);
        setLoading(false);
      });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      resetPassword();
    }
  };

  return (
    <div className="tw-min-h-screen tw-flex tw-flex-col lg:tw-flex-row">
      {/* Left Panel - Branding */}
      <div className="tw-hidden lg:tw-flex lg:tw-w-1/2 tw-bg-primary tw-flex-col tw-justify-between tw-p-12">
        <div>
          <img
            src={getWhiteLabelLogo()}
            alt="Logo"
            className="tw-h-10 tw-w-auto"
          />
        </div>
        <div className="tw-space-y-4">
          <h1 className="tw-text-4xl tw-font-bold tw-text-primary-foreground">
            {strings("forgotPassword.title")}
          </h1>
          <p className="tw-text-lg tw-text-primary-foreground/80">
            {strings("forgotPassword.subtitle")}
          </p>
        </div>
        <p className="tw-text-sm tw-text-primary-foreground/60">
          © {new Date().getFullYear()} All rights reserved.
        </p>
      </div>

      {/* Right Panel - Reset Form */}
      <div className="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-bg-background tw-p-6 lg:tw-p-12">
        <div className="tw-w-full tw-max-w-md tw-space-y-8">
          {/* Mobile Logo */}
          <div className="lg:tw-hidden tw-flex tw-justify-center">
            <div className="tw-bg-primary tw-p-4 tw-rounded-xl">
              <img
                src={getWhiteLabelLogo()}
                alt="Logo"
                className="tw-h-8 tw-w-auto"
              />
            </div>
          </div>

          <div className="tw-text-center lg:tw-text-left">
            <h2 className="tw-text-2xl tw-font-bold tw-tracking-tight">
              {strings("forgotPassword.resetTitle")}
            </h2>
            <p className="tw-text-muted-foreground tw-mt-2">
              {strings("forgotPassword.resetSubtitle")}
            </p>
          </div>

          <form onSubmit={resetPassword} className="tw-space-y-6">
            {message && (
              <Alert variant="destructive">
                <AlertCircle className="tw-h-4 tw-w-4" />
                <AlertDescription>{message}</AlertDescription>
              </Alert>
            )}

            <div className="tw-space-y-2">
              <Label htmlFor="email">{strings("placeholders.email")}</Label>
              <div className="tw-relative">
                <Mail className="tw-absolute tw-left-3 tw-top-3 tw-h-4 tw-w-4 tw-text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder={strings("placeholders.emailExample")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="tw-pl-10 tw-h-11"
                  autoComplete="email"
                  autoFocus={true}
                />
              </div>
            </div>

            {/* Invisible reCAPTCHA container */}
            <div id="recaptcha-container"></div>

            <Button
              type="submit"
              className="tw-w-full tw-h-11"
              disabled={loading}
            >
              {loading && (
                <Loader2 className="tw-mr-2 tw-h-4 tw-w-4 tw-animate-spin" />
              )}
              {strings("forgotPassword.sendLink")}
            </Button>

            <Link
              to={routes.LOGIN_SCREEN}
              className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-text-sm tw-text-muted-foreground hover:tw-text-foreground tw-transition-colors"
            >
              <ArrowLeft className="tw-h-4 tw-w-4" />
              {strings("forgotPassword.backToSignIn")}
            </Link>
          </form>

          <p className="tw-text-center tw-text-xs tw-text-muted-foreground lg:tw-hidden">
            {strings("forgotPassword.rememberPassword")}{" "}
            <Link
              to={routes.LOGIN_SCREEN}
              className="tw-text-primary hover:tw-underline"
            >
              {strings("buttons.signIn")}
            </Link>
          </p>
        </div>
      </div>

      {/* Success Dialog */}
      <ResponsiveModal
        open={isEmailSent}
        onOpenChange={setIsEmailSent}
        title={strings("forgotPassword.checkEmail")}
        maxWidth="tw-max-w-md"
        onConfirm={() => navigate(routes.LOGIN_SCREEN)}
        confirmLabel={strings("forgotPassword.backToSignIn")}
      >
        <div className="tw-text-center">
          <div className="tw-mx-auto tw-mb-4 tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-full tw-bg-green-100">
            <CheckCircle2 className="tw-h-6 tw-w-6 tw-text-green-600" />
          </div>
          <p className="tw-text-muted-foreground">
            {strings("forgotPassword.emailSentDesc", { email })}
          </p>
        </div>
      </ResponsiveModal>
    </div>
  );
};

export default connector(ForgotPasswordScreen);
